/**
 * MachinePage -- Machine detail view.
 *
 * Peripherals, machine info (inline-editable), toner, reviews, cash in the
 * machine, live Core status, kiosk logs and recent events. All live data comes
 * from the v2 Core heartbeat; the legacy push-agent fields are gone.
 * Auto-refreshes every 30 seconds.
 */

import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useMachine } from '../hooks/useMachine';
import { useEvents } from '../hooks/useEvents';
import { updateMachine, purgeMachine, archiveMachine } from '../api/client';
import { StatusBadge } from '../components/StatusBadge';
import { EventLog } from '../components/EventLog';
import { KioskLogs } from '../components/KioskLogs';
import { CoreStatus } from '../components/CoreStatus';
import { MachineFeedback } from '../components/MachineFeedback';
import { MachineCash } from '../components/MachineCash';

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return 'Never';
  return new Date(dateStr).toLocaleString();
}

/** canEdit=false — роль operator: карточка без Edit/Delete. */
export function MachinePage({ canEdit = true, canDelete = false }: { canEdit?: boolean; canDelete?: boolean }) {
  const { id } = useParams<{ id: string }>();
  const machineId = Number(id);

  const { machine, loading, error, refresh } = useMachine(machineId);
  const { events, loading: eventsLoading } = useEvents({ machineId: machine?.machineId, limit: 20 });

  // Inline edit state
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [editLocation, setEditLocation] = useState('');
  const [editGoogle, setEditGoogle] = useState('');
  const [editLowPaper, setEditLowPaper] = useState(50);
  const [editLowToner, setEditLowToner] = useState(10);
  const [editOfflineTimeout, setEditOfflineTimeout] = useState(10);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  function startEditing() {
    if (!machine) return;
    setEditName(machine.name);
    setEditLocation(machine.location ?? '');
    setEditGoogle(machine.googleReviewUrl ?? '');
    setEditLowPaper(machine.lowPaperThreshold);
    setEditLowToner(machine.lowTonerPercent);
    setEditOfflineTimeout(Math.round(machine.offlineTimeoutMs / 60000));
    setEditing(true);
    setSaveError(null);
  }

  async function handleSave() {
    if (!machine) return;
    setSaving(true);
    setSaveError(null);
    try {
      await updateMachine(machine.machineId, {
        name: editName,
        location: editLocation || undefined,
        googleReviewUrl: editGoogle.trim() || null,
        lowPaperThreshold: editLowPaper,
        lowTonerPercent: editLowToner,
        offlineTimeoutMs: editOfflineTimeout * 60000,
      } as any);
      setEditing(false);
      refresh();
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  // Loading
  if (loading && !machine) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-6">
          <Link to="/" className="btn btn-secondary btn-sm">&larr; Back</Link>
          <h2>Machine #{id}</h2>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <h3>Loading machine data...</h3>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error
  if (error || !machine) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-6">
          <Link to="/" className="btn btn-secondary btn-sm">&larr; Back</Link>
          <h2>Machine #{id}</h2>
        </div>
        <div className="card">
          <div className="card-body">
            <div className="empty-state">
              <h3>Failed to load machine</h3>
              <p className="text-muted">{error ?? 'Machine not found.'}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-2 mb-6" style={{ flexWrap: 'wrap' }}>
        <Link to="/" className="btn btn-secondary btn-sm">&larr; Back</Link>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, wordBreak: 'break-word' }}>{machine.name}</h2>
            <StatusBadge online={machine.isOnline} />
          </div>
          {machine.location && (
            <div className="text-secondary" style={{ fontSize: '0.88rem', marginTop: 2 }}>
              {machine.location}
            </div>
          )}
        </div>
      </div>

      {/* Peripherals (v2 Core / agent inventory) */}
      {machine.peripherals?.devices && machine.peripherals.devices.length > 0 && (
        <div className="detail-grid mb-6">
          <div className="card">
            <div className="card-header">
              <div className="flex items-center justify-between">
                <span>Peripherals</span>
                {machine.peripheralsUpdatedAt && (
                  <span className="text-muted" style={{ fontSize: '0.72rem', fontWeight: 400 }}>
                    {formatDate(machine.peripheralsUpdatedAt)}
                  </span>
                )}
              </div>
            </div>
            <div className="card-body">
              <table style={{ fontSize: '0.85rem', width: '100%' }}>
                <tbody>
                  {machine.peripherals.devices.map((d) => {
                    const ok = d.present && (d.status === 'OK' || d.status === '');
                    const color = ok
                      ? 'var(--color-online)'
                      : d.optional
                        ? 'var(--color-text-muted)'
                        : 'var(--color-offline)';
                    const label = ok ? 'OK' : d.present ? d.status : d.optional ? 'n/a' : 'missing';
                    return (
                      <tr key={d.name}>
                        <td style={{ width: 16, paddingRight: 4 }}>
                          <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: color }} />
                        </td>
                        <td>{d.label}</td>
                        <td style={{ textAlign: 'right', color, fontWeight: 500 }}>{label}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Machine Info Section — always visible */}
      <div className="card mb-6">
        <div className="card-header">
          <div className="flex items-center justify-between">
            <span>Machine Info</span>
            {!editing && canEdit && (
              <div className="flex gap-2">
                <button className="btn btn-secondary btn-sm" onClick={startEditing} style={{ fontSize: '0.78rem' }}>
                  Edit
                </button>
                {canDelete && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '0.78rem' }}
                    title="Hide this machine from the machine list, the public site and offline alerts. Reversible from the Archive page."
                    onClick={async () => {
                      if (!confirm(`Archive machine ${machine.name} (ID ${machine.machineId})?\n\nIt disappears from the machine list, the site and offline alerts. Data stays; restore it from the Archive page.`)) return;
                      try {
                        await archiveMachine(machine.machineId);
                        window.location.href = '/archive';
                      } catch (e) { alert(e instanceof Error ? e.message : 'Archive failed'); }
                    }}
                  >
                    Archive
                  </button>
                )}
                {canDelete && (
                  <button
                    className="btn btn-secondary btn-sm"
                    style={{ fontSize: '0.78rem', background: '#ef4444', color: 'white', borderColor: '#ef4444' }}
                    title="Erase this machine and everything tied to it: sessions, events, logs, heartbeat, alerts, pricing"
                    onClick={async () => {
                      const typed = prompt(
                        `Erase machine ${machine.name} (ID ${machine.machineId}) from the database?\n\n` +
                        `This deletes ALL its data: sessions (incl. refund queue and reports), events, ` +
                        `kiosk logs, heartbeat, alerts, version history, per-machine pricing.\n` +
                        `It cannot be undone.\n\nType the machine ID to confirm:`,
                      );
                      if (typed !== String(machine.machineId)) return;
                      try {
                        const r = await purgeMachine(machine.machineId);
                        const lines = Object.entries(r.deleted).filter(([, n]) => n > 0).map(([t, n]) => `${t}: ${n}`);
                        alert(`Erased machine ${machine.machineId}.\n\n${lines.join('\n') || 'nothing else referenced it'}`);
                        window.location.href = '/';
                      } catch (e) { alert(e instanceof Error ? e.message : 'Delete failed'); }
                    }}
                  >
                    Erase from database
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="card-body">
          {editing ? (
                <div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Name</label>
                    <input
                      className="form-input"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Location</label>
                    <input
                      className="form-input"
                      value={editLocation}
                      onChange={(e) => setEditLocation(e.target.value)}
                      placeholder="e.g. Prague, Main Street"
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Google review link</label>
                    <input
                      className="form-input"
                      value={editGoogle}
                      onChange={(e) => setEditGoogle(e.target.value)}
                      placeholder="https://search.google.com/local/writereview?placeid=…"
                    />
                    <div className="text-muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
                      The kiosk QR points to /g/{machine.machineId}, which redirects here. Empty = no review QR on this machine.
                    </div>
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Low Paper Threshold (sheets)</label>
                    <input
                      className="form-input"
                      type="number"
                      value={editLowPaper}
                      onChange={(e) => setEditLowPaper(parseInt(e.target.value, 10) || 0)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 12 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Low Toner Threshold (%)</label>
                    <input
                      className="form-input"
                      type="number"
                      value={editLowToner}
                      onChange={(e) => setEditLowToner(parseInt(e.target.value, 10) || 0)}
                    />
                  </div>
                  <div className="form-group" style={{ marginBottom: 16 }}>
                    <label style={{ fontSize: '0.82rem', fontWeight: 600 }}>Offline Timeout (minutes)</label>
                    <input
                      className="form-input"
                      type="number"
                      value={editOfflineTimeout}
                      onChange={(e) => setEditOfflineTimeout(parseInt(e.target.value, 10) || 10)}
                    />
                  </div>
                  {saveError && (
                    <div style={{ color: '#ef4444', fontSize: '0.85rem', marginBottom: 12 }}>
                      {saveError}
                    </div>
                  )}
                  <div className="flex gap-2">
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving ? 'Saving...' : 'Save'}
                    </button>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setEditing(false)}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <table className="info-table">
                  <tbody>
                    <tr>
                      <td className="text-secondary" style={{ width: '40%' }}>Machine ID</td>
                      <td>{machine.machineId}</td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Kiosk (Core) Version</td>
                      <td>
                        {machine.kioskVersion ? (
                          <>
                            {machine.kioskVersion.version}
                            {machine.kioskVersion.result === 'rollback' && (
                              <span style={{ color: '#dc2626', fontWeight: 600 }}> (rollback)</span>
                            )}
                            <span className="text-secondary" style={{ fontSize: '0.8rem' }}>
                              {' '}· {formatDate(machine.kioskVersion.reportedAt)}
                            </span>
                          </>
                        ) : (
                          'Not reported'
                        )}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Last Seen</td>
                      <td>{formatDate(machine.lastSeenAt)}</td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Registered</td>
                      <td>{formatDate(machine.createdAt)}</td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Google review</td>
                      <td>
                        {machine.googleReviewUrl
                          ? <><a href={machine.googleReviewUrl} target="_blank" rel="noreferrer">link set</a> <span className="text-muted">· {machine.googleReviewClicks ?? 0} scans</span></>
                          : <span className="text-muted">not set — no review QR on this kiosk</span>}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Low Paper Alert</td>
                      <td>{machine.lowPaperThreshold} sheets</td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Low Toner Alert</td>
                      <td>{machine.lowTonerPercent}%</td>
                    </tr>
                    <tr>
                      <td className="text-secondary">Offline Timeout</td>
                      <td>{Math.round(machine.offlineTimeoutMs / 60000)} min</td>
                    </tr>
                  </tbody>
                </table>
              )}
        </div>
      </div>

      {/* Remote Control / Remote Exec / Emergency reboot убраны 2026-09-14:
          очереди commands / exec_commands / reboot_requests на киоске никто не
          читает после выпила GCP, кнопки лишь рапортовали успех. Вернуть,
          когда Core начнёт читать очереди. */}

      {/* Расходники: спрашиваем принтеры по SNMP раз в сутки. Карточка на
          главной показывает тонер только когда он на исходе — здесь уровень
          видно всегда, иначе непонятно, собираются ли данные вообще. */}
      <div className="card mb-6">
        <div className="card-header">
          Supplies (toner)
          {machine.suppliesAt && (
            <span className="text-secondary" style={{ fontSize: '0.8rem', fontWeight: 400 }}>
              {' '}· polled {formatDate(machine.suppliesAt)}
            </span>
          )}
        </div>
        <div className="card-body">
          {machine.supplies && machine.supplies.length > 0 ? (
            <table className="table">
              <tbody>
                {machine.supplies.map((s, i) => (
                  <tr key={i}>
                    <td className="text-secondary">{s.name}</td>
                    <td style={{ width: '6rem', textAlign: 'right' }}>
                      {typeof s.percent === 'number' ? (
                        <span
                          style={{
                            fontWeight: 600,
                            color:
                              s.percent <= 10 ? '#dc2626' : s.percent <= 20 ? '#d97706' : undefined,
                          }}
                        >
                          {s.percent}%
                        </span>
                      ) : (
                        <span className="text-secondary">unknown</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <p className="text-muted">No toner data yet — the kiosk reports it once a day.</p>
            </div>
          )}
        </div>
      </div>

      {/* Отзывы покупателей с экрана «Hotovo» */}
      <MachineFeedback machineId={machine.machineId} />

      {/* Касса: что должно лежать внутри (гроссбух Core) + история инкассаций */}
      <MachineCash machineId={machine.machineId} />

      {/* Core hardware/status heartbeat */}
      <CoreStatus machineId={machine.machineId} />

      {/* Kiosk Core logs */}
      <KioskLogs machineId={machine.machineId} />

      {/* Recent Events */}
      <div className="card mb-6">
        <div className="card-header">Recent Events</div>
        <div className="card-body">
          {eventsLoading ? (
            <div className="empty-state">
              <p className="text-muted">Loading events...</p>
            </div>
          ) : (
            <EventLog events={events} />
          )}
        </div>
      </div>
    </div>
  );
}
